import { Component } from "@theme/component";

export class MultipleImageUploadComponent extends Component {
  /** @type {HTMLElement | null} */
  container = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  imagePlaceholders = null;
  /** @type {Map<HTMLElement, any>} */
  placeholderData = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
  }

  cacheElements() {
    // Get the parent container that holds all the elements
    this.container = this.closest("[data-block-id]") || this;

    // Get image upload elements
    const imageSection = this.container.querySelector(".image-upload-trigger");
    if (imageSection) {
      this.imagePlaceholders = imageSection.querySelectorAll(
        ".image-upload-placeholder"
      );
    }
  }

  bindEvents() {
    if (!this.imagePlaceholders) return;

    this.imagePlaceholders.forEach((placeholder) => {
      this.setupImagePlaceholder(placeholder);
    });
  }

  /**
   * @param {HTMLElement} placeholder
   */
  setupImagePlaceholder(placeholder) {
    const uploadButton = placeholder.querySelector(".image-upload-btn");
    const removeBtn = placeholder.querySelector(".remove-btn");
    const imagePlaceholder = placeholder.querySelector(".image-placeholder");
    const imagePreview = imagePlaceholder?.querySelector(".uploaded-image");

    // Store placeholder data
    const placeholderInfo = {
      uploadButton,
      removeBtn,
      imagePlaceholder,
      imagePreview,
      currentFile: null,
      imageInput: null,
    };

    this.placeholderData.set(placeholder, placeholderInfo);

    // Bind upload event
    if (uploadButton) {
      uploadButton.addEventListener("click", (e) =>
        this.handleUploadClick(e, placeholder)
      );
    }

    // Bind remove event
    if (removeBtn) {
      removeBtn.addEventListener("click", (e) =>
        this.handleRemoveClick(e, placeholder)
      );
    }
  }

  /**
   * @param {Event} e
   * @param {HTMLElement} placeholder
   */
  handleUploadClick(e, placeholder) {
    const data = this.placeholderData.get(placeholder);
    if (!data || !data.uploadButton) return;

    const index = data.uploadButton.getAttribute("image-input-index");
    if (!index) return;

    // Find the file input
    data.imageInput = data.uploadButton.querySelector(`input[type="file"]`);
    if (data.imageInput) {
      data.imageInput.click();

      // Add change listener if not already added
      if (!data.imageInput.hasAttribute("data-listener-added")) {
        data.imageInput.setAttribute("data-listener-added", "true");
        data.imageInput.addEventListener(
          "change",
          /** @param {Event} changeEvent */ (changeEvent) => {
            this.handleFileChange(changeEvent, placeholder);
          }
        );
      }
    }
  }

  /**
   * @param {Event} e
   * @param {HTMLElement} placeholder
   */
  handleFileChange(e, placeholder) {
    const data = this.placeholderData.get(placeholder);
    if (!data || !e.target) return;

    const target = /** @type {HTMLInputElement} */ (e.target);
    const file = target.files?.[0];

    if (!file) return;

    // Validate file type
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      alert("Please select a PNG or JPG file");
      if (data.removeBtn) data.removeBtn.style.display = "none";
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      if (data.removeBtn) data.removeBtn.style.display = "none";
      return;
    }

    data.currentFile = file;

    // Update preview
    this.updateImagePreview(placeholder, file);

    // Update file input for form submission
    this.updateFileInput(placeholder, file);

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("image-uploaded", {
        detail: { file, placeholder },
        bubbles: true,
      })
    );
  }

  /**
   * @param {HTMLElement} placeholder
   * @param {File} file
   */
  updateImagePreview(placeholder, file) {
    const data = this.placeholderData.get(placeholder);
    if (!data) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (data.imagePreview && e.target?.result) {
        data.imagePreview.src = /** @type {string} */ (e.target.result);
        data.imagePreview.style.display = "block";

        // Hide upload icon
        const uploadIcon = data.imagePlaceholder?.querySelector(".upload-icon");
        if (uploadIcon) {
          /** @type {HTMLElement} */ (uploadIcon).style.display = "none";
        }

        // Show remove button
        if (data.removeBtn) {
          data.removeBtn.style.display = "block";
        }
      }
    };
    reader.readAsDataURL(file);
  }

  /**
   * @param {HTMLElement} placeholder
   * @param {File} file
   */
  updateFileInput(placeholder, file) {
    const data = this.placeholderData.get(placeholder);
    if (!data || !data.imageInput) return;

    // Update file input for form submission
    const dt = new DataTransfer();
    dt.items.add(file);
    data.imageInput.files = dt.files;
  }

  /**
   * @param {Event} e
   * @param {HTMLElement} placeholder
   */
  handleRemoveClick(e, placeholder) {
    e.stopPropagation();

    const data = this.placeholderData.get(placeholder);
    if (!data) return;

    // Hide image preview
    if (data.imagePreview) {
      data.imagePreview.style.display = "none";
    }

    // Clear file input
    if (data.imageInput) {
      data.imageInput.value = "";
    }

    // Show upload icon
    const uploadIcon = data.imagePlaceholder?.querySelector(".upload-icon");
    if (uploadIcon) {
      /** @type {HTMLElement} */ (uploadIcon).style.display = "block";
    }

    // Hide remove button
    if (data.removeBtn) {
      data.removeBtn.style.display = "none";
    }

    // Clear current file
    data.currentFile = null;

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("image-removed", {
        detail: { placeholder },
        bubbles: true,
      })
    );
  }

  cleanup() {
    // Clean up event listeners and data
    this.placeholderData.clear();
  }

  // Public API methods

  /**
   * @param {number} index
   * @returns {File | null}
   */
  getUploadedFile(index) {
    if (
      !this.imagePlaceholders ||
      index < 1 ||
      index > this.imagePlaceholders.length
    ) {
      return null;
    }

    const placeholder = this.imagePlaceholders[index - 1];
    if (!placeholder) return null;

    const data = this.placeholderData.get(placeholder);
    return data?.currentFile || null;
  }

  /**
   * @returns {File[]}
   */
  getAllUploadedFiles() {
    /** @type {File[]} */
    const files = [];
    this.placeholderData.forEach((data) => {
      if (data.currentFile) {
        files.push(data.currentFile);
      }
    });
    return files;
  }

  /**
   * @param {number} index
   */
  removeFile(index) {
    if (
      !this.imagePlaceholders ||
      index < 1 ||
      index > this.imagePlaceholders.length
    ) {
      return;
    }

    const placeholder = this.imagePlaceholders[index - 1];
    if (!placeholder) return;

    const mockEvent = /** @type {Event} */ ({
      stopPropagation: () => {},
    });
    this.handleRemoveClick(mockEvent, placeholder);
  }

  /**
   * Clear all uploaded files
   */
  clearAllFiles() {
    if (!this.imagePlaceholders) return;

    this.imagePlaceholders.forEach((placeholder, index) => {
      this.removeFile(index + 1);
    });
  }

  /**
   * @returns {number}
   */
  getUploadedFileCount() {
    return this.getAllUploadedFiles().length;
  }

  /**
   * @returns {boolean}
   */
  hasUploadedFiles() {
    return this.getUploadedFileCount() > 0;
  }
}

if (!customElements.get("multiple-image-upload-component")) {
  customElements.define(
    "multiple-image-upload-component",
    MultipleImageUploadComponent
  );
}
